import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type { supplierFormData } from "@/schema/schema";

export const SearchSupplierModal = ({
  isOpen,
  onClose,
  onSelectSupplier,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectSupplier: (supplier: supplierFormData) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: suppliers,
    isLoading,
    error,
  } = useQuery({
      queryKey: ["suppliers", searchTerm],
      queryFn: async () => {
        const response = await api.get<supplierFormData[]>("/suppliers", {
          params: {
            search: searchTerm,
          }
        })
        return response.data;
      },
      select: (data) =>
        data.filter(
          (item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase())
        ),
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Search Supplier</DialogTitle>
          <DialogDescription>
            Search for supplier's with name or tin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by name or tin..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
              aria-label="Search items"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white">
                <TableRow>
                  <TableHead className="w-[150px]">Code</TableHead>
                  <TableHead className="w-[150px]">Name</TableHead>
                  <TableHead className="w-[150px]">Registration Type</TableHead>
                  <TableHead className="w-[200px]">Registration No.</TableHead>
                  <TableHead className="w-[200px]">Email</TableHead>
                  <TableHead className="w-[150px]">Contact</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-red-500"
                      >
                        Error loading items
                      </TableCell>
                    </TableRow>
                  ) : suppliers?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    suppliers?.map((supplier: supplierFormData) => (
                      <TableRow key={supplier.id} className="hover:bg-muted">
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {supplier.code}
                        </TableCell>
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {supplier.name}
                        </TableCell>
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {supplier.registrationType}
                        </TableCell>
                        <TableCell className="w-[200px] max-w-[200px] truncate">
                          {supplier.registrationNumber}
                        </TableCell>
                        <TableCell className="w-[200px] max-w-[200px] truncate">
                          {supplier.email}
                        </TableCell>
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {supplier.contactNumber}
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            size="sm"
                            onClick={() => {
                              onSelectSupplier({
                                code: supplier.code,
                                id: supplier.id,
                                name: supplier.name,
                                email: supplier.email,
                                contactNumber: supplier.contactNumber,
                                tin: supplier.tin,
                                registrationType: supplier.registrationType,
                                registrationNumber: supplier.registrationNumber,
                                sstRegistrationNumber: supplier.sstRegistrationNumber,
                                tourismTaxRegistrationNumber:supplier.tourismTaxRegistrationNumber,
                                msicCode: supplier.msicCode,
                                businessActivityDescription: supplier.businessActivityDescription,
                                defaultCurrency:supplier.defaultCurrency,
                                address:{
                                  addressLine0: supplier.address.addressLine0,
                                  addressLine1: supplier.address.addressLine1,  
                                  addressLine2: supplier.address.addressLine2,
                                  postalZone: supplier.address.postalZone,
                                  cityName: supplier.address.cityName,
                                  state: supplier.address.state,
                                  country: supplier.address.country,
                                }
                              });
                              onClose();
                            }}
                            aria-label={`Select ${supplier.name}`}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchSupplierModal;
